import Space from '@rebass/space'
import { Link } from 'gatsby'
import { useQueryParam, ModalStateParam } from 'use-query-params'
import React from 'react'
import styled from 'styled-components'
import { Flex, Box } from '@rebass/grid'
import Logo from './Logo'
import Card from './Card'
import Button from './Button'
import TrialDownloadButton from './TrialDownloadButton'

import SetappLogo from './logos/SetappLogo'

const StickyNavigation = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: transparent;
`

const ProgressiveBlurContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  width: 100%;
  height: 120px;
  pointer-events: none;
  z-index: 999;
`

const BlurFilter = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  &:nth-child(1) {
    backdrop-filter: blur(16px);
    mask: linear-gradient(
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 25%,
      rgba(0, 0, 0, 0) 50%
    );
  }

  &:nth-child(2) {
    backdrop-filter: blur(8px);
    mask: linear-gradient(
      rgba(0, 0, 0, 1) 15%,
      rgba(0, 0, 0, 1) 40%,
      rgba(0, 0, 0, 0) 65%
    );
  }

  &:nth-child(3) {
    backdrop-filter: blur(4px);
    mask: linear-gradient(
      rgba(0, 0, 0, 1) 30%,
      rgba(0, 0, 0, 1) 55%,
      rgba(0, 0, 0, 0) 80%
    );
  }

  &:nth-child(4) {
    backdrop-filter: blur(2px);
    mask: linear-gradient(
      rgba(0, 0, 0, 1) 45%,
      rgba(0, 0, 0, 1) 70%,
      rgba(0, 0, 0, 0) 90%
    );
  }

  &:nth-child(5) {
    backdrop-filter: blur(1px);
    mask: linear-gradient(
      rgba(0, 0, 0, 1) 60%,
      rgba(0, 0, 0, 1) 85%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  /* Fallback for browsers that don't support backdrop-filter */
  @supports not (backdrop-filter: blur(1px)) {
    display: none;
  }
`

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    transparent 70%,
    ${(props) =>
        props.theme.appearance === 'light'
          ? 'rgba(255, 255, 255, 0.3)'
          : 'rgba(52, 54, 57, 0.3)'}
      85%,
    ${(props) =>
        props.theme.appearance === 'light'
          ? 'rgba(255, 255, 255, 0.7)'
          : 'rgba(52, 54, 57, 0.7)'}
      95%,
    ${(props) => (props.theme.appearance === 'light' ? '#FFF' : '#343639')} 100%
  );
`

const StyledMenu = styled.div`
  display: inline-block;
  position: relative;

  a {
    position: relative;
    font-weight: 600;
    color: ${(props) =>
      props.theme.appearance === 'light'
        ? '#31383c'
        : 'rgba(255, 255, 255, .9)'};
    text-decoration: none;
    padding: 0 0.5rem;

    &:first-child {
      padding-left: 0;
    }

    &:hover,
    &[aria-current='page'] {
      color: ${(props) =>
        props.theme.appearance === 'light'
          ? '#748086'
          : 'rgba(255, 255, 255, .75)'};
    }
  }
`

const StyledSubMenu = styled(Card)`
  position: absolute;
  opacity: 0;
  visibility: hidden;
  top: 32px;
  transform: perspective(400px) rotateX(-4deg);
  left: 0;
  height: auto;
  transition: all 0.35s ease-in-out;
  z-index: 9999;
  min-width: 360px !important;
  text-align: left;

  /* * {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
  } */

  > * {
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    &:last-child {
      border-bottom: 0;
    }
  }

  p {
    font-weight: 400;
  }

  svg {
    max-width: inherit !important;
  }
`

const ResponsiveBox = styled(Box)`
  @media (max-width: 768px) {
    text-align: left;
  }
  @media (min-width: 769px) {
    text-align: right;
  }
`

const Navigation = (props) => {
  const [modalState, setModalState] = useQueryParam(
    'thank-you',
    ModalStateParam,
  )

  function analyticsDownloadTrial() {
    setModalState('true')
  }

  return (
    <StickyNavigation>
      <Flex flexWrap="wrap" alignItems="center" p={[4, 3]}>
        {/* Logo */}
        <Box width={[1, 1 / 5]}>
          <Link to="/">
            <Logo />
          </Link>
        </Box>

        {/* Menubar */}
        <ResponsiveBox width={[1, 4 / 5]} mt={[3, 0]}>
          <StyledMenu>
            <Link to="/">Home</Link>
            <Link
              to="/icon-sets/"
              getProps={({ location }) => {
                return location.pathname.startsWith('/icon-sets')
                  ? { 'aria-current': 'page' }
                  : null
              }}>
              Icon sets
            </Link>
            <Link to="/updates/">Updates</Link>
            <Link to="/pricing/">Pricing</Link>
          </StyledMenu>

          <Box display={['none', 'inline-block']}>
            <Space mx={2}>
              <TrialDownloadButton
                onClick={analyticsDownloadTrial}
                colorscheme="white"
                className="xs-hide"
                location="nav"
              />
            </Space>
            <Space mx={2}>
              <Button
                as="a"
                href="https://go.setapp.com/stp177?refAppID=264&utm_medium=vendor_program&utm_source=IconJar&utm_content=link&utm_campaign=pricing_page_callout&stc=main_nav"
                className="setapp-button"
                style={{
                  backgroundColor: 'rgba(38, 38, 44, 1)',
                  background:
                    'linear-gradient( 180deg ,  0% rgba(255, 255, 255, 0.12) ,  100% rgba(255, 255, 255, 0) )',
                  boxShadow:
                    '0 1px 1px 0 rgba(0, 0, 0, 0.04) ,  0 1px 2px 0 rgba(0, 0, 0, 0.03) ,  0 2px 3px 0 rgba(0, 0, 0, 0.03) ,  0 -1px 0 0 inset rgba(0, 0, 0, 0.04) ,  0 -1px 2px 0 inset rgba(0, 0, 0, 0.05)',
                }}>
                <div>
                  <SetappLogo />{' '}
                  <span style={{ display: 'inline-block', marginLeft: '4px' }}>
                    Use with Setapp
                  </span>
                </div>
              </Button>
            </Space>
          </Box>
        </ResponsiveBox>
      </Flex>
    </StickyNavigation>
  )
}

// Export the progressive blur components for use in Layout
export { ProgressiveBlurContainer, BlurFilter, GradientOverlay }
export default Navigation
